import React, { useCallback, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import FilterBar from '../../../../components/filter-bar';
import { useEnhancedReturnsContext } from '../grids/enhanced-returns-context';
import staticCriteriaArguments from '../grids/filters/static-criteria-arguments';
import EnhancedReturnsCriteriaOperatorsMap from '../grids/filters/custom-criteria-operators';
import { mapEnhancedReturnsFiltersToRequestBody } from '../map-enhanced-returns-to-api-params';
import { emptyArray } from 'ui-components/default-props/empty';
import { RETURNS_GRID_CLEAR_FILTER, RETURNS_GRID_SELECT_FILTER } from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import { buildStoreFilter, STORE_GUIDS } from '../grids/filters/options/store';
import Global from '../../../../store/global-selectors';
import { ALL_STORES_STORE_GUID, useEnhancedReturnRouteMatch } from '../../../../store/returns/returns.helpers';
import createDate from '../grids/filters/options/create-date';
import returnExchangeType from '../grids/filters/options/return-exchange-type';
import refundMethod from '../grids/filters/options/refund-method';
import fulfillmentStatuses from '../grids/filters/options/fulfillmentStatuses';
import amount from '../grids/filters/options/amount';

import style from '../grids/filter-bar.module.less';

// SSR filters (without approval type - that's for legacy enhanced returns only)
const ssrFilterReferences = [returnExchangeType, refundMethod, createDate, fulfillmentStatuses, amount];

export const filterBar = 'returnsFilterBar';

type SSRFilterBarProps = {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  viewSpecificFilters?: readonly any[];
};

export const SSRFilterBar = ({ viewSpecificFilters = emptyArray }: SSRFilterBarProps) => {
  const { updateFilters } = useEnhancedReturnsContext();
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const [appliedFilters, setAppliedFilters] = useState<any[]>([]);
  const { storeGuid } = useEnhancedReturnRouteMatch();
  const stores = useSelector(Global.selectSellerActiveStores);

  const isAllStoresView = storeGuid === ALL_STORES_STORE_GUID;

  const onFiltersChanged = useCallback(
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    (newlyAppliedFilters: any[]) => {
      setAppliedFilters(newlyAppliedFilters);

      const apiFilters = mapEnhancedReturnsFiltersToRequestBody(newlyAppliedFilters);
      Segment.trackEvent(RETURNS_GRID_SELECT_FILTER, {
        type: RETURNS_GRID_SELECT_FILTER,
        newlyAppliedFilters,
        apiFilters,
      });

      updateFilters(apiFilters);
    },
    [updateFilters],
  );

  const clearFilters = useCallback(() => {
    Segment.trackEvent(RETURNS_GRID_CLEAR_FILTER, {
      type: RETURNS_GRID_CLEAR_FILTER,
    });

    setAppliedFilters([]);
    updateFilters({});
  }, [updateFilters]);

  // Only show store filter when viewing all-stores
  const storeFilter = useMemo(() => (isAllStoresView ? buildStoreFilter(stores) : null), [isAllStoresView, stores]);

  const storeCriteriaArguments = useMemo(() => {
    if (!isAllStoresView) {
      return staticCriteriaArguments;
    }
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const storeOptions = stores.map((s: any) => ({ id: s.storeGuid, displayName: s.name, value: s.storeGuid }));
    return { ...staticCriteriaArguments, [STORE_GUIDS]: storeOptions };
  }, [isAllStoresView, stores]);

  const popularFilterByOptions = useMemo(
    () =>
      [...ssrFilterReferences, ...viewSpecificFilters, ...(storeFilter ? [storeFilter] : [])].map((filterBy) => ({
        ...filterBy,
        displayName: filterBy.displayName(appliedFilters),
      })),
    [viewSpecificFilters, appliedFilters, storeFilter],
  );

  return (
    <div className={style['filter-bar-wrapper']}>
      <FilterBar
        allFilterBys={popularFilterByOptions}
        appliedFilters={appliedFilters}
        clearFilters={clearFilters}
        criteriaArgumentsMap={storeCriteriaArguments}
        criteriaOperatorsMap={EnhancedReturnsCriteriaOperatorsMap}
        onFiltersChanged={onFiltersChanged}
        popularFilterBys={popularFilterByOptions}
        shouldUseOverflow={false}
        showOtherFilter={false}
      />
      <div className={style['filter-bar-actions-wrapper']} />
    </div>
  );
};
